import { proxy } from 'valtio';
import type { Justification, CompanyInfo } from '../types/soa';
import { api } from '../utils/api';
import { debounce } from 'lodash';

const defaultCompanyInfo: CompanyInfo = {
  issueNo: '1.00',
  date: new Date().toISOString().split('T')[0],
  companyName: 'Your Company Name',
  logo: '',
  preparedBy: '',
  reviewedBy: '',
  approvedBy: '',
  approvedDate: '',
  nextReviewDate: '',
  versionHistory: [],
};

export const soaStore = proxy<{
  justifications: Record<string, Justification>;
  companyInfo: CompanyInfo;
  loading: boolean;
  showUsed: boolean;
  showUnused: boolean;
  filterSection: string;
  searchTerm: string;
}>({
  justifications: {},
  companyInfo: { ...defaultCompanyInfo },
  loading: true,
  showUsed: true,
  showUnused: true,
  filterSection: 'all',
  searchTerm: '',
});

// Track last-known server timestamp outside reactive proxy to avoid save loops
let companyInfoLastSavedAt = '';

// Debounced save for company info with conflict detection
const debouncedSaveCompanyInfo = debounce(async () => {
  try {
    const payload = { ...JSON.parse(JSON.stringify(soaStore.companyInfo)), _updatedAt: companyInfoLastSavedAt };
    const response = await api.put('/soa/company-info', payload) as Record<string, unknown>;
    if (response._updatedAt !== undefined) {
      companyInfoLastSavedAt = (response._updatedAt as string) || '';
    }
  } catch (e: unknown) {
    const err = e as Error & { status?: number };
    if (err.status === 409) {
      console.warn('SoA company info conflict detected, reloading...');
      await soaActions.loadAll();
    } else {
      console.error('Failed to save company info to API:', e);
    }
  }
}, 500);

export const soaActions = {
  loadAll: async () => {
    try {
      soaStore.loading = true;
      const [justifications, companyInfo] = await Promise.all([
        api.get('/soa/justifications') as Promise<Record<string, Justification> | null>,
        api.get('/soa/company-info') as Promise<(CompanyInfo & { _updatedAt?: string }) | null>,
      ]);
      soaStore.justifications = justifications || {} as Record<string, Justification>;
      if (companyInfo) {
        // Extract _updatedAt before assigning to store (use !== undefined so '' is handled)
        if (companyInfo._updatedAt !== undefined) {
          companyInfoLastSavedAt = companyInfo._updatedAt || '';
          delete companyInfo._updatedAt;
        }
        soaStore.companyInfo = { ...defaultCompanyInfo, ...companyInfo, versionHistory: companyInfo.versionHistory || [] };
      }
    } catch (e) {
      console.error('Failed to load SoA data from API:', e);
      // Fallback to localStorage
      try {
        const justData = localStorage.getItem('iso27001-justifications');
        if (justData) soaStore.justifications = JSON.parse(justData);
        const companyData = localStorage.getItem('soa-company-info');
        if (companyData) soaStore.companyInfo = JSON.parse(companyData);
      } catch {}
    } finally {
      soaStore.loading = false;
    }
  },

  setJustification: async (control: string, justification: string) => {
    soaStore.justifications[control] = {
      justification,
      updatedAt: new Date().toISOString()
    };
    try {
      await api.put(`/soa/justifications/${encodeURIComponent(control)}`, { justification });
    } catch (e) {
      console.error('Failed to save justification to API:', e);
    }
  },

  deleteJustification: async (control: string) => {
    delete soaStore.justifications[control];
    try {
      await api.del(`/soa/justifications/${encodeURIComponent(control)}`);
    } catch (e) {
      console.error('Failed to delete justification from API:', e);
    }
  },

  setCompanyInfo: (info: Partial<CompanyInfo>) => {
    soaStore.companyInfo = { ...soaStore.companyInfo, ...info };
    debouncedSaveCompanyInfo();
  },

  toggleShowUsed: () => { soaStore.showUsed = !soaStore.showUsed; },
  toggleShowUnused: () => { soaStore.showUnused = !soaStore.showUnused; },
  setFilterSection: (section: string) => { soaStore.filterSection = section; },
  setSearchTerm: (term: string) => { soaStore.searchTerm = term; },
};
